import React, { useState, useEffect } from "react";
import {
  Work,
  CheckCircle,
  Error,
  Person,
  Upload,
  CloudUpload,
} from "@mui/icons-material";
import { CircularProgress } from "@mui/material";
import api from "../config/api";

const MAX_FILE_MB = 5;
const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024;
const ALLOWED_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

const CareerForm = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    experience: "",
    current_company: "",
    how_you_found_us: "",
  });

  const [resumeFile, setResumeFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const getErrorMessage = (error) => {
    const data = error?.response?.data;

    if (!data) return "Submission failed. Please try again.";

    if (typeof data?.detail === "string") return data.detail;

    if (typeof data === "object") {
      const firstKey = Object.keys(data)[0];
      const val = data[firstKey];
      if (Array.isArray(val) && val.length) return val[0];
      if (typeof val === "string") return val;
    }

    return "Submission failed. Please try again.";
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    setErrorMsg("");
    setSuccessMsg("");

    if (!file) {
      setResumeFile(null);
      return;
    }

    if (file.size > MAX_FILE_BYTES) {
      setResumeFile(null);
      setErrorMsg(`Resume must be under ${MAX_FILE_MB}MB.`);
      return;
    }

    if (!ALLOWED_TYPES.includes(file.type)) {
      setResumeFile(null);
      setErrorMsg("Only PDF, DOC, or DOCX files are allowed.");
      return;
    }

    setResumeFile(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    setLoading(true);
    setSuccessMsg("");
    setErrorMsg("");

    // Resume is required by UI
    if (!resumeFile) {
      setLoading(false);
      setErrorMsg("Please upload your resume (PDF/DOC/DOCX).");
      return;
    }

    const data = new FormData();
    Object.keys(formData).forEach((key) => data.append(key, formData[key]));
    data.append("resume", resumeFile);

    try {
      await api.post("careers/", data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setSuccessMsg("Application submitted successfully!");
      setFormData({
        name: "",
        email: "",
        phone: "",
        experience: "",
        current_company: "",
        how_you_found_us: "",
      });
      setResumeFile(null);

      // reset file input (optional)
      const el = document.getElementById("file-upload");
      if (el) el.value = "";
    } catch (error) {
      console.error("Backend error response:", error.response?.data);
      setErrorMsg(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/10 via-accent to-primary/10 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        {/* Header Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-primary to-primary/80 rounded-full mb-6">
            <Work className="w-8 h-8 text-secondary" />
          </div>
          <h1 className="text-4xl font-bold text-secondary mb-4">
            Join Our Team
          </h1>
          <p className="text-xl text-secondary max-w-md mx-auto">
            Take the next step in your career journey with us. We're excited to
            hear from you!
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-secondary rounded-2xl shadow-xl overflow-hidden">
          <div className="bg-primary text-secondary overflow-hidden p-8">
            <h2 className="text-2xl font-bold">Career Application</h2>
            <p className="text-secondary mt-2">
              Fill out the form below to get started
            </p>
          </div>

          <div className="p-8">
            {/* Success/Error Messages */}
            {successMsg && (
              <div className="mb-6 p-4 bg-primary/10 border border-primary/20 rounded-lg">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <CheckCircle className="h-5 w-5 text-primary" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-primary">
                      {successMsg}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {errorMsg && (
              <div className="mb-6 p-4 bg-primary/10 border border-primary/20 rounded-lg">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <Error className="h-5 w-5 text-primary" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-primary">
                      {errorMsg}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6 text-accent">
              {/* Personal Information Section */}
              <div>
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <Person className="w-5 h-5 text-primary mr-2" />
                  Personal Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      placeholder="Enter your full name"
                      className="w-full placeholder:text-secondary/80 text-secondary px-4 py-3 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition duration-200"
                      required
                      value={formData.name}
                      onChange={handleChange}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      placeholder="your.email@example.com"
                      className="w-full placeholder:text-secondary/80 text-secondary px-4 py-3 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition duration-200"
                      required
                      value={formData.email}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="text"
                    name="phone"
                    placeholder="+1 (555) 123-4567"
                    className="w-full placeholder:text-secondary/80 text-secondary px-4 py-3 border border-secondary/20 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition duration-200"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </div>
              </div>

              {/* Professional Information Section */}
              <div>
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <Work className="w-5 h-5 text-primary mr-2" />
                  Professional Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Years of Experience *
                    </label>
                    <input
                      type="text"
                      name="experience"
                      placeholder="e.g., 3 years"
                      className="w-full placeholder:text-secondary/80 text-secondary px-4 py-3 border border-secondary/20 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition duration-200"
                      required
                      value={formData.experience}
                      onChange={handleChange}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Current Company
                    </label>
                    <input
                      type="text"
                      name="current_company"
                      placeholder="Your current employer"
                      className="w-full placeholder:text-secondary/80 text-secondary px-4 py-3 border border-secondary/20 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition duration-200"
                      value={formData.current_company}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium mb-2">
                    How did you find us?
                  </label>
                  <input
                    type="text"
                    name="how_you_found_us"
                    placeholder="LinkedIn, Google, Referral, etc."
                    className="w-full placeholder:text-secondary/80 text-secondary px-4 py-3 border border-secondary/20 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition duration-200"
                    value={formData.how_you_found_us}
                    onChange={handleChange}
                  />
                </div>
              </div>

              {/* Resume Upload Section */}
              <div>
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <Upload className="w-5 h-5 text-primary mr-2" />
                  Resume Upload
                </h3>
                <div className="mt-2">
                  <label className="block text-sm font-medium mb-2">
                    Resume/CV *
                  </label>
                  <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-lg border-primary transition duration-200">
                    <div className="space-y-1 text-center">
                      <CloudUpload className="mx-auto h-12 w-12 text-accent" />
                      <div className="flex text-sm">
                        <label
                          htmlFor="file-upload"
                          className="relative cursor-pointer rounded-md font-bold text-accent hover:text-primary/90 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary"
                        >
                          <span>Upload a file</span>
                          <input
                            id="file-upload"
                            name="file-upload"
                            type="file"
                            accept=".pdf,.doc,.docx"
                            onChange={handleFileChange}
                            required
                            className="sr-only"
                          />
                        </label>
                        <p className="pl-1">or drag and drop</p>
                      </div>
                      <p className="text-xs">PDF, DOC, or DOCX up to 5MB</p>
                      {resumeFile && (
                        <div className="text-medium font-semibold p-2 border border-accent rounded-md mt-4">
                          ✓ {resumeFile.name}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="pt-6">
                <button
                  type="submit"
                  className="w-full bg-primary text-secondary px-8 py-4 rounded-lg font-semibold text-lg hover:from-primary/90 hover:to-primary/70 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transform hover:scale-105 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  disabled={loading}
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <CircularProgress
                        size={20}
                        className="-ml-1 mr-3 text-secondary"
                      />
                      Submitting Application...
                    </div>
                  ) : (
                    "Submit Application"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Footer Note */}
        <div className="text-center mt-8">
          <p className="text-secondary/80 text-sm">
            We'll review your application and get back to you within 3-5
            business days.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CareerForm;
